import { useRef, useState } from "react";
import { Camera, Check, Info, Plus, Trash2, Upload, X } from "lucide-react";

import { scanPaspoort } from "../../afstand/afstandApi";

/**
 * Het entingenboekje van één dier: foto's maken en de uitgelezen gegevens controleren.
 *
 * <p><b>De eigenaar controleert, de uitlezing stelt alleen voor.</b> Dat is geen beleefdheid maar de
 * kern: een automatische uitlezing van een handgeschreven boekje leest datums verkeerd, en een
 * verkeerd gelezen vaccinatiedatum is het verschil tussen "ingeënt" en "niet ingeënt". Niets wordt
 * vastgelegd voordat iemand ernaar gekeken heeft, en elk veld blijft aanpasbaar.
 *
 * <p><b>Een uitlezing is geen bewijs.</b> Ook wanneer alles netjes herkend is, blijft dit wat de
 * eigenaar opgeeft. Het asiel beoordeelt de vaccinatiestatus zelf; die zin staat daarom in beeld.
 *
 * <p><b>De originele foto's blijven bewaard.</b> Ze gaan mee naar het asiel, naast de ingevoerde
 * gegevens — zodat een dierenarts daar het boekje zelf kan nalezen in plaats van op de overtikking
 * te moeten vertrouwen.
 *
 * <p><b>Meerdere pagina's.</b> Een boekje is zelden één bladzijde, en de vaccinaties staan vaak
 * verspreid.
 */

/** Eén regel uit het boekje, zoals de eigenaar hem bevestigt. */
export interface Enting {
  id: string;
  vaccin: string;
  datum: string;
  geldigTot: string;
  dierenarts: string;
}

/** De bladzijden en de bevestigde entingen van één dier. */
export interface Boekje {
  paginas: { id: string; naam: string; bron: string; bestand: File }[];
  entingen: Enting[];
  /** Of de eigenaar de regels heeft nagelopen; zonder dat gaan ze niet mee. */
  gecontroleerd: boolean;
}

export const LEEG_BOEKJE: Boekje = { paginas: [], entingen: [], gecontroleerd: false };

export function AfstandEntingenboekje({ dierNaam, opvangSleutel, boekje, zetBoekje, onSluit }: {
  dierNaam: string;
  /** Het uitlezen gaat via het asiel; dat heeft de dienst, niet dit portaal. */
  opvangSleutel: string;
  boekje: Boekje;
  zetBoekje: (b: Boekje) => void;
  onSluit: () => void;
}) {
  const [fase, setFase] = useState<"fotos" | "controle">(
    boekje.paginas.length > 0 ? "controle" : "fotos",
  );
  const camera = useRef<HTMLInputElement>(null);
  const bestanden = useRef<HTMLInputElement>(null);
  const [fout, setFout] = useState<string | null>(null);
  const [leest, setLeest] = useState(false);
  const [uitlezing, setUitlezing] = useState<string | null>(null);

  const MAX = 10 * 1024 * 1024;

  const voegPaginasToe = (lijst: FileList | null) => {
    if (!lijst) return;
    const nieuw = [...boekje.paginas];
    for (const bestand of Array.from(lijst)) {
      if (bestand.size > MAX) {
        setFout(`"${bestand.name}" is groter dan 10 MB en is overgeslagen.`);
        continue;
      }
      nieuw.push({
        id: `p-${nieuw.length}-${Math.random().toString(36).slice(2, 8)}`,
        naam: bestand.name,
        // Alleen om de foto te tonen; wordt vrijgegeven zodra de bladzijde weggaat.
        bron: URL.createObjectURL(bestand),
        bestand,
      });
    }
    zetBoekje({ ...boekje, paginas: nieuw });
  };

  const haalPaginaWeg = (id: string) => {
    const weg = boekje.paginas.find((p) => p.id === id);
    if (weg) URL.revokeObjectURL(weg.bron);
    zetBoekje({ ...boekje, paginas: boekje.paginas.filter((p) => p.id !== id) });
  };

  /**
   * De bladzijden laten lezen door het asiel.
   *
   * <p>Wat eruit komt is een <em>voorstel</em>: het verschijnt als regels die de eigenaar nog kan
   * aanpassen, en de controle staat op onbevestigd tot hij ernaar gekeken heeft. Lukt het uitlezen
   * niet, dan is dat geen fout maar een mededeling -- hij tikt het dan zelf over.
   */
  const laatLezen = async () => {
    setFase("controle");
    if (boekje.entingen.length > 0) {
      // Er staat al iets; dat niet overschrijven met een nieuwe uitlezing.
      return;
    }
    setLeest(true);
    setUitlezing(null);
    try {
      const uitkomst = await scanPaspoort(opvangSleutel, boekje.paginas.map((p) => p.bestand));
      if (!uitkomst.beschikbaar || uitkomst.entingen.length === 0) {
        setUitlezing(uitkomst.beschikbaar
          ? "We konden er geen vaccinaties uit halen. Neem ze hieronder zelf over."
          : "Automatisch uitlezen is nu niet beschikbaar. Neem de vaccinaties hieronder zelf over.");
        return;
      }
      setUitlezing(`${uitkomst.entingen.length} vaccinatie(s) gevonden. Controleer ze hieronder.`);
      zetBoekje({
        ...boekje,
        gecontroleerd: false,
        entingen: uitkomst.entingen.map((e, i) => ({
          id: `v-${i}-${Math.random().toString(36).slice(2, 8)}`,
          vaccin: e.vaccin ?? "",
          datum: e.datum ?? "",
          geldigTot: e.geldigTot ?? "",
          dierenarts: "",
        })),
      });
    } catch {
      setUitlezing("Automatisch uitlezen lukte niet. Neem de vaccinaties hieronder zelf over.");
    } finally {
      setLeest(false);
    }
  };

  const nieuweEnting = (): Enting => ({
    id: `e-${boekje.entingen.length}-${Math.random().toString(36).slice(2, 8)}`,
    vaccin: "",
    datum: "",
    geldigTot: "",
    dierenarts: "",
  });

  const werkEnting = (id: string, verandering: Partial<Enting>) =>
    zetBoekje({
      ...boekje,
      entingen: boekje.entingen.map((e) => (e.id === id ? { ...e, ...verandering } : e)),
      // Een wijziging maakt de controle ongedaan: wat net veranderd is, is nog niet bevestigd.
      gecontroleerd: false,
    });

  return (
    <div className="afst__venster" role="dialog" aria-modal="true"
         aria-label={`Entingenboekje ${dierNaam}`}>
      <div className="afst__venster-blad">
        <header className="afst__venster-kop">
          <div>
            <h3>
              {fase === "fotos"
                ? `Entingenboekje toevoegen · ${dierNaam}`
                : `Controleer de uitgelezen entingen · ${dierNaam}`}
            </h3>
            <p>
              {fase === "fotos"
                ? "Maak of upload foto's van het boekje. Meerdere bladzijden mogen."
                : "Controleer en pas de gegevens aan voordat u ze bevestigt."}
            </p>
          </div>
          <button type="button" className="afst__venster-sluit" aria-label="Sluiten" onClick={onSluit}>
            <X size={20} aria-hidden="true" />
          </button>
        </header>

        <div className="afst__venster-inhoud">
          {fase === "fotos" && (
            <div className="afst__boekje">
              <div className="afst__boekje-paginas">
                {boekje.paginas.map((p, i) => (
                  <figure key={p.id} className="afst__pagina">
                    <img src={p.bron} alt={`Bladzijde ${i + 1}`} />
                    <figcaption>
                      Bladzijde {i + 1}
                      <button type="button" aria-label="Deze bladzijde weghalen"
                              onClick={() => haalPaginaWeg(p.id)}>
                        <Trash2 size={15} aria-hidden="true" />
                      </button>
                    </figcaption>
                  </figure>
                ))}

                {boekje.paginas.length === 0 && (
                  <p className="afst__boekje-leeg">
                    Nog geen bladzijden toegevoegd. Fotografeer de pagina's waarop de vaccinaties
                    staan.
                  </p>
                )}
              </div>

              {fout && <p className="afst__melding afst__melding--fout" role="alert">{fout}</p>}

              <div className="afst__boekje-knoppen">
                {/* `capture` opent op een telefoon meteen de camera; op een computer is het een
                    gewone bestandskeuze, en dat is daar ook wat je wilt. */}
                <button type="button" className="afst__knop" onClick={() => camera.current?.click()}>
                  <Camera size={18} aria-hidden="true" />
                  Foto maken
                </button>
                <button type="button" className="afst__knop afst__knop--rustig"
                        onClick={() => bestanden.current?.click()}>
                  <Upload size={18} aria-hidden="true" />
                  Bestand kiezen
                </button>
                <input ref={camera} type="file" hidden accept="image/*" capture="environment"
                       onChange={(e) => { voegPaginasToe(e.target.files); e.target.value = ""; }} />
                <input ref={bestanden} type="file" hidden multiple accept="image/jpeg,image/png"
                       onChange={(e) => { voegPaginasToe(e.target.files); e.target.value = ""; }} />
              </div>

              <p className="afst__tip">
                <span className="afst__tipicoon" aria-hidden="true"><Info size={15} /></span>
                Zorg dat de bladzijde goed leesbaar is: recht van boven, zonder schaduw over de
                datums. De originele foto's blijven bij dit dier bewaard.
              </p>
            </div>
          )}

          {fase === "controle" && (
            <div className="afst__boekje-controle">
              <aside className="afst__boekje-bron">
                {boekje.paginas[0] && <img src={boekje.paginas[0].bron} alt="Foto van het boekje" />}
                <p>
                  {boekje.paginas.length} bladzijde{boekje.paginas.length === 1 ? "" : "n"} ·
                  {" "}<span className="afst__bijlage-klaar">Bij het dier opgeslagen</span>
                </p>
              </aside>

              <div className="afst__boekje-regels">
                {leest && <p className="afst__melding">Bezig met uitlezen…</p>}
                {uitlezing && <p className="afst__melding">{uitlezing}</p>}

                {!leest && boekje.entingen.length === 0 && (
                  <p className="afst__melding">
                    Er zijn nog geen vaccinaties ingevuld. Neem ze over van de foto; het asiel kan
                    het boekje er zelf naast leggen.
                  </p>
                )}

                {boekje.entingen.map((e, i) => (
                  <fieldset key={e.id} className="afst__enting">
                    <legend>Vaccinatie {i + 1}</legend>
                    <div className="afst__raster">
                      <Entingveld label="Naam vaccin" waarde={e.vaccin}
                                  zet={(v) => werkEnting(e.id, { vaccin: v })} />
                      <Entingveld label="Vaccinatiedatum" type="date" waarde={e.datum}
                                  zet={(v) => werkEnting(e.id, { datum: v })} />
                      <Entingveld label="Geldig tot" type="date" waarde={e.geldigTot}
                                  zet={(v) => werkEnting(e.id, { geldigTot: v })} />
                      <Entingveld label="Dierenarts" waarde={e.dierenarts}
                                  zet={(v) => werkEnting(e.id, { dierenarts: v })} />
                    </div>
                    <button type="button" className="afst__weghalen"
                            onClick={() => zetBoekje({
                              ...boekje,
                              entingen: boekje.entingen.filter((x) => x.id !== e.id),
                              gecontroleerd: false,
                            })}>
                      <Trash2 size={15} aria-hidden="true" />
                      Deze vaccinatie weghalen
                    </button>
                  </fieldset>
                ))}

                <button type="button" className="afst__knop afst__knop--rustig"
                        onClick={() => zetBoekje({
                          ...boekje,
                          entingen: [...boekje.entingen, nieuweEnting()],
                          gecontroleerd: false,
                        })}>
                  <Plus size={18} aria-hidden="true" />
                  Vaccinatie toevoegen
                </button>

                <p className="afst__wnoot">
                  Onzekere, ontbrekende of verkeerd gelezen velden blijven aanpasbaar. Een
                  automatische herkenning is geen bewijs van een geldige vaccinatie; het asiel
                  beoordeelt dat zelf.
                </p>
              </div>
            </div>
          )}
        </div>

        <footer className="afst__venster-voet">
          {fase === "controle" ? (
            <button type="button" className="afst__knop afst__knop--rustig"
                    onClick={() => setFase("fotos")}>
              Terug naar foto's
            </button>
          ) : (
            <button type="button" className="afst__knop afst__knop--rustig" onClick={onSluit}>
              Annuleren
            </button>
          )}

          {fase === "fotos" ? (
            <button type="button" className="afst__knop" disabled={boekje.paginas.length === 0 || leest}
                    onClick={() => void laatLezen()}>
              {leest ? "Bezig met uitlezen…" : "Foto's bewaren en uitlezen"}
            </button>
          ) : (
            <button type="button" className="afst__knop"
                    onClick={() => { zetBoekje({ ...boekje, gecontroleerd: true }); onSluit(); }}>
              Controleren en opslaan
              <Check size={18} aria-hidden="true" />
            </button>
          )}
        </footer>
      </div>
    </div>
  );
}

function Entingveld({ label, waarde, zet, type = "text" }: {
  label: string;
  waarde: string;
  zet: (v: string) => void;
  type?: string;
}) {
  return (
    <label className="afst__veld afst__veld--half">
      <span>{label}</span>
      <span className="afst__invoer">
        <input type={type} value={waarde} onChange={(e) => zet(e.target.value)} />
      </span>
    </label>
  );
}
